<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
    :fullscreen="$vuetify.display.xs"
    :width="$vuetify.display.smAndUp ? 600 : 'auto'"
    open-on-click
  >
    <template v-slot:activator="{ props }">
      <slot name="activator" v-bind="props" data-cy="list-podItem-actions-deletePod-activator" />
    </template>

    <div class="vb-dialog vb-dialog-danger">
      <!-- The Regular delete POD card -->
      <v-card v-if="!asset" data-cy="dialog-deletePod-modal">
        <v-card-title class="d-flex justify-space-between" data-cy="dialog-deletePod-title">
          <span>{{ $t('routes.node.confirmDeletion') }}</span>

          <v-btn icon @click="dialog = false" :aria-label="$t('common.close')">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-5" data-cy="dialog-deletePod-podName">
          <div v-if="errorMessage" class="bg-error">
            {{ errorMessage }}
          </div>
          <div v-if="errorTranslatable" class="bg-error">
            {{ $replaceT(errorTranslatable.code, errorTranslatable.params, errorTranslatable.content) }}
          </div>
          <div>{{ $t('routes.node.deletePodDialogMessage') }}</div>
          <div class="py-4">
            <v-icon>mdi-file-document</v-icon>
            {{ pod.name }}
          </div>

          <div v-if="!asset">
            <v-checkbox
              v-model="deletePermanently"
              density="compact"
              hide-details
              :label="$t('routes.node.deleteDialogDeletePermanently')"
            />
            <div v-if="deletePermanently" class="mt-2 tw-warning">
              <v-icon>mdi-alert-box</v-icon>
              <span>{{ $t('routes.node.deletePodDeletePermanentlyWarning') }}</span>
            </div>
          </div>

          <div class="mt-2 tw-warning">
            <v-icon>mdi-alert-box</v-icon>
            <span>{{ $t('routes.node.deletePodRelationsInfo') }}</span>
          </div>
          <div class="mt-2 tw-warning">
            <v-icon>mdi-alert-box</v-icon>
            <span>{{ $t('routes.node.deleteDialogArchivedFilesCannotBeRestored') }}</span>
          </div>

        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn elevation=0 @click="deletePod" :disabled="deletionInProgress" class="btnOk"
                 data-cy="dialog-deletePod-ok">{{ $t('common.delete') }}
          </v-btn>
          <v-btn variant="text" @click="dialog = false" class="btnCancel"
                 data-cy="dialog-deletePod-false">{{ $t('common.cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>

      <!-- The delete ASSET of a pod card -->
      <v-card v-if="asset" data-cy="dialog-deleteAsset-modal">
        <v-card-title class="d-flex justify-space-between"
                      data-cy="dialog-deleteAsset-rawAssetMessage">
          <span>{{ $t('routes.node.confirmDeletion') }}</span>
          <v-btn icon @click="dialog = false" :aria-label="$t('common.close')">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-5" data-cy="dialog-deleteAsset-assetName">
          <div v-if="errorMessage" class="bg-error">
            {{ errorMessage }}
          </div>
          <div v-if="errorTranslatable" class="bg-error">
            {{ $replaceT(errorTranslatable.code, errorTranslatable.params, errorTranslatable.content) }}
          </div>

          <div>{{ $t('routes.node.deletePodAssetDialogMessage') }}</div>

          <div class="py-6">
            <v-icon>mdi-file-document</v-icon>
            {{ asset.name }}
          </div>

          <div v-if="isRawAsset" class="tw-warning">
            <v-icon>mdi-alert-box</v-icon>
            <span>{{ $t('routes.node.deletePodRawAssetDialogMessage') }}</span>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn elevation=0 @click="deletePodAsset" :disabled="deletionInProgress" class="btnOk"
                 data-cy="dialog-deleteAsset-ok">
            {{ $t('common.delete') }}
          </v-btn>
          <v-btn variant="text" @click="dialog = false" class="btnCancel"
                 data-cy="dialog-deleteAsset-false">{{ $t('common.cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </div>
  </v-dialog>
</template>

<script>
import { usePodStore } from '@/stores/PodStore.js';
import DialogActionKeys from '@/components/mixin/DialogActionKeys.js';
import DialogModel from '@/components/mixin/DialogModel.js';

export default {
  mixins: [DialogActionKeys, DialogModel],
  name: "DeletePodDialog",
  props: {
    pod: {
      type: Object,
      required: true,
    },
    asset: {
      type: Object,
      required: false
    }
  },
  data: function() {
    return {
      errorMessage: '',
      errorTranslatable: '',
      deletionInProgress: false,
      deletePermanently: false,
    }
  },
  watch: {
    dialog: function() {
      this.errorMessage = '';
      this.errorTranslatable = '';
      this.deletionInProgress = false;
      this.deletePermanently = false;
    }
  },
  methods: {
    async deletePod() {
      this.deletionInProgress = true;
      const podStore = usePodStore();

      try {
        await this.$nextTick();
        podStore.addLatestDeletedPodId(this.pod.id);

        await podStore.deletePods([ this.pod.id ], this.deletePermanently);

        if (!this.deletePermanently) {
          podStore.refreshRecycleBin();
        }
        this.dialog = false;
      } catch (error) {
        if (error && error.response && error.response.data && error.response.data.error) {
          const apiError = error.response.data.error.message;
          if (typeof apiError === 'object') {
            this.errorTranslatable = apiError;
          } else {
            this.errorMessage = apiError;
          }
        } else {
          this.errorMessage = 'Error!';
        }
      } finally {
        this.deletionInProgress = false;
      }
    },
    async deletePodAsset() {
      const nodeID = this.pod.nodeid;
      const podStore = usePodStore();
      this.deletionInProgress = true;

      try {
        if (this.isRawAsset) {
          await podStore.deletePods([ this.pod.id ], this.deletePermanently);
          await this.$router.push({ path: '/node/' + nodeID });
          this.dialog = false;
        } else {
          await podStore.deletePodAssets([ { id: this.asset.id, podid: this.asset.podid } ]);
          this.dialog = false;
        }
      } catch (error) {
        if (error && error.response && error.response.data && error.response.data.error) {
          const apiError = error.response.data.error.message;
          if (typeof apiError === 'object') {
            this.errorTranslatable = apiError;
          } else {
            this.errorMessage = apiError;
          }
        } else {
          this.errorMessage = 'Error!';
        }
      } finally {
        this.deletionInProgress = false;
      }
    },
  },
  computed: {
    isRawAsset: function() {
      return  this.asset && this.pod && this.pod.asset &&
              this.pod.asset.raw && this.pod.asset.raw[0] &&
              this.asset.id === this.pod.asset.raw[0].id;
    }
  }
}
</script>

<style>

</style>
